"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { Onborda, OnbordaProvider, useOnborda } from "onborda";
import { userOnboardingSteps, hasSeenUserTour, markUserTourAsSeen } from "@/lib/user-onboarding-tour";
import { TourCard } from "./admin/tour-card";

interface UserOnboardingProviderProps {
  children: React.ReactNode;
}

// Controller component that uses the Onborda context
function UserOnboardingController() {
  const { startOnborda, closeOnborda, isOnbordaVisible, currentStep } = useOnborda();
  const pathname = usePathname();
  const isUserDashboard = pathname === "/dashboard";

  // Listen for custom tour completion event
  useEffect(() => {
    const handleTourComplete = () => {
      markUserTourAsSeen();
      sessionStorage.removeItem("user-tour-was-active");
      closeOnborda();
    };

    window.addEventListener('user-tour-complete', handleTourComplete);
    return () => window.removeEventListener('user-tour-complete', handleTourComplete);
  }, [closeOnborda]);

  // Add overlay click handler to dismiss tour
  useEffect(() => {
    if (!isOnbordaVisible) return;

    const handleOverlayClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click was on the overlay itself (not on the card or pointer)
      if (target.getAttribute('data-name') === 'onborda-overlay') {
        markUserTourAsSeen();
        sessionStorage.removeItem("user-tour-was-active");
        closeOnborda();
      }
    };

    // Add event listener after a small delay to ensure overlay is mounted
    const timer = setTimeout(() => {
      document.addEventListener('click', handleOverlayClick, true);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleOverlayClick, true);
    };
  }, [isOnbordaVisible, closeOnborda]);

  // Monitor when the tour is closed/completed to mark as seen
  useEffect(() => {
    if (!isOnbordaVisible && currentStep === -1) {
      const wasTourActive = sessionStorage.getItem("user-tour-was-active");
      if (wasTourActive === "true") {
        markUserTourAsSeen();
        sessionStorage.removeItem("user-tour-was-active");
      }
    }
  }, [isOnbordaVisible, currentStep]);

  useEffect(() => {
    // Only show tour on the user dashboard
    if (!isUserDashboard) {
      if (isOnbordaVisible) {
        closeOnborda();
      }
      return;
    }

    // Ensure we're on the client side before checking localStorage
    if (typeof window === "undefined") return;

    // Check if user has seen the tour
    const hasSeenTour = hasSeenUserTour();

    // Debug logging (can be removed in production)
    console.log("[User Tour] Checking tour status:", { hasSeenTour, isOnbordaVisible });

    // Only start tour if user hasn't seen it AND tour is not already visible
    if (hasSeenTour) {
      console.log("[User Tour] Tour already completed, skipping");
      return;
    }

    // Small delay to ensure DOM elements are mounted
    const timer = setTimeout(() => {
      if (!isOnbordaVisible) {
        console.log("[User Tour] Starting tour");
        sessionStorage.setItem("user-tour-was-active", "true");
        startOnborda("user-onboarding");
      }
    }, 1000); // Slightly longer delay for user dashboard

    return () => clearTimeout(timer);
  }, [isUserDashboard, pathname, startOnborda, closeOnborda, isOnbordaVisible]);

  // This component doesn't render anything
  return null;
}

export function UserOnboardingProvider({ children }: UserOnboardingProviderProps) {
  // Wrap steps in the tour format required by Onborda v1.2.3+
  const tours = [
    {
      tour: "user-onboarding",
      steps: userOnboardingSteps,
    },
  ];

  return (
    <>
      <style jsx global>{`
        /* Onborda Portal positioning - must cover entire viewport */
        [data-radix-portal] {
          position: fixed !important;
          inset: 0 !important;
          z-index: 100000 !important;
          pointer-events: none !important;
        }
        
        /* Onborda overlay - covers full viewport */
        [data-name="onborda-overlay"] {
          position: fixed !important;
          inset: 0 !important;
          z-index: 100000 !important;
          pointer-events: auto !important;
          width: 100vw !important;
          height: 100vh !important;
        }
        
        /* Onborda pointer (spotlight) */
        [data-name="onborda-pointer"] {
          position: absolute !important;
          z-index: 100001 !important;
          pointer-events: none !important;
        }
        
        /* Onborda wrapper should allow overflow */
        [data-name="onborda-wrapper"] {
          position: relative !important;
        }
        
        /* Ensure tour card stays within viewport */
        [data-radix-portal] > div > div {
          max-height: 80vh !important;
          max-width: 90vw !important;
          overflow: visible !important;
        }
        
        /* Force card to be visible and within bounds */
        [data-radix-portal] [role="dialog"],
        [data-radix-portal] > div > div > * {
          max-height: 80vh !important;
          overflow-y: auto !important;
        }
      `}</style>
      <OnbordaProvider>
        <Onborda
          steps={tours}
          shadowRgb="139, 92, 246"
          shadowOpacity="0.8"
          cardComponent={TourCard}
        >
          <UserOnboardingController />
          {children}
        </Onborda>
      </OnbordaProvider>
    </>
  );
}

